import HttpFactory from "~/repository/factory";
import type {
  StudentAgreement,
  StudentAgreementPreparation,
  StudentAgreementPreparationInput,
} from "~/types/studentAgreements";

const agreements = "/api/documents/student-agreements/";

export default class StudentAgreementsModule extends HttpFactory {
  list(studentId?: number) {
    return this.get<StudentAgreement[]>(
      agreements,
      studentId ? { student_id: studentId } : {},
    );
  }

  getAgreement(id: number) {
    return this.get<StudentAgreement>(`${agreements}${id}/`);
  }

  createFromGenerated(studentId: number, documentId: number, title?: string) {
    const body = new FormData();
    body.append("student_id", String(studentId));
    body.append("generated_document_id", String(documentId));
    if (title) body.append("title", title);
    return this.post<StudentAgreement>(agreements, body);
  }

  createFromFile(studentId: number, file: File, title?: string) {
    const body = new FormData();
    body.append("student_id", String(studentId));
    body.append("source_file", file);
    if (title) body.append("title", title);
    return this.post<StudentAgreement>(agreements, body);
  }

  createPreparation(input: StudentAgreementPreparationInput) {
    return this.post<StudentAgreementPreparation>(
      `${agreements}preparations/`,
      input,
    );
  }

  downloadPreparationPreview(id: string) {
    return this.get<Blob>(
      `${agreements}preparations/${id}/preview/`,
      {},
      { responseType: "blob" },
    );
  }

  createFromPreparation(
    studentId: number,
    preparationId: string,
    title?: string,
  ) {
    return this.post<StudentAgreement>(agreements, {
      student_id: studentId,
      preparation_id: preparationId,
      ...(title ? { title } : {}),
    });
  }

  download(id: number) {
    return this.get<Blob>(
      `${agreements}${id}/download/`,
      {},
      { responseType: "blob" },
    );
  }

  downloadSigned(id: number, submissionVersion?: number) {
    return this.get<Blob>(
      `${agreements}${id}/signed-file/`,
      submissionVersion ? { version: submissionVersion } : {},
      { responseType: "blob" },
    );
  }

  uploadSigned(id: number, file: File) {
    const body = new FormData();
    body.append("file", file);
    return this.post<StudentAgreement>(`${agreements}${id}/signed-file/`, body);
  }

  confirm(id: number) {
    return this.post<StudentAgreement>(`${agreements}${id}/confirm/`, {});
  }

  requestChanges(id: number, reason: string) {
    return this.post<StudentAgreement>(`${agreements}${id}/request-changes/`, {
      reason,
    });
  }
}
